import { chakra } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { focusRing } from '../primitives/_shared';

const Root = chakra('div', { base: { minWidth: 0 } });

const HeaderButton = chakra('button', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '2',
    width: 'full',
    textAlign: 'left',
    bg: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'text.primary',
    fontFamily: 'body',
    minWidth: 0,
    borderRadius: 'sm',
    _hover: { color: 'text.primary' },
    ...focusRing,
  },
});

const Chevron = chakra(ChevronRight, {
  base: {
    flexShrink: 0,
    color: 'text.muted',
    transitionProperty: 'transform',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    '[data-open="true"] > &': { transform: 'rotate(90deg)' },
  },
});

const SummarySlot = chakra('span', {
  base: { display: 'flex', alignItems: 'center', gap: '2', minWidth: 0, flex: '1 1 auto' },
});

const Body = chakra('div', { base: { minWidth: 0 } });

export interface DisclosureProps {
  open: boolean;
  onToggle: () => void;
  /** Header content — use inline elements (`Text as="span"`), it lives inside a button. */
  summary: ReactNode;
  /** Body content; UNMOUNTED while closed (kept out of the DOM). */
  children: ReactNode;
  /** Testid on the body region (present only while open). */
  bodyTestId?: string;
  /** Hide the leading chevron (e.g. a text-only trigger). */
  hideChevron?: boolean;
  chevronAtEnd?: boolean;
  'data-testid'?: string;
  'data-kind'?: string;
}

/**
 * An accessible disclosure: a button header (`aria-expanded`) over a body that is
 * MOUNTED only while open — so collapsed content is truly absent from the DOM (matches
 * the transcript's unmount-on-collapse behaviour and keeps long lists light). The
 * chevron rotates on open; reduced-motion is honoured globally.
 */
export const Disclosure = forwardRef<HTMLDivElement, DisclosureProps>(function Disclosure(
  { open, onToggle, summary, children, bodyTestId, hideChevron, chevronAtEnd, ...rest },
  ref,
) {
  return (
    <Root ref={ref} {...rest}>
      <HeaderButton type="button" aria-expanded={open} data-open={open} onClick={onToggle}>
        {!hideChevron && !chevronAtEnd && <Chevron size={16} strokeWidth={2.5} aria-hidden />}
        <SummarySlot>{summary}</SummarySlot>
        {!hideChevron && chevronAtEnd && <Chevron size={16} strokeWidth={2.5} aria-hidden />}
      </HeaderButton>
      {open && <Body data-testid={bodyTestId}>{children}</Body>}
    </Root>
  );
});
